/**
 * Component deduplication utilities for Thesys C1 components.
 * 
 * ⚠️ AI AGENT NOTE - CRITICAL REQUIREMENT:
 * When updating UI components, we MUST UPDATE existing components, NOT deduplicate them.
 * 
 * - Use `mergeComponents()` to merge new components with existing ones - it UPDATES matching components
 * - Do NOT filter out components that have already been rendered - they need to be UPDATED
 * - The backend explicitly requires: "CRITICAL we update ui compuntens we do not deduplicate"
 * - Components may be updated with new data (e.g., summaries, additional props) and must replace old versions
 * 
 * `deduplicateComponents()` is only for removing exact duplicates within a single array,
 * NOT for preventing updates to existing components in the UI.
 */

import type { C1Component, C1ComponentObject } from '../types/c1Types';
import { isC1ComponentObject } from '../types/c1Types';

/**
 * Try to extract a valid C1Component from various possible formats.
 * Handles components that might be wrapped in objects or arrays.
 */
function extractComponent(component: unknown): C1Component | null {
  if (component === null || component === undefined) {
    return null;
  }
  
  // If it's already a string, try to parse and validate
  if (typeof component === 'string') {
    try {
      const parsed = JSON.parse(component);
      if (isC1ComponentObject(parsed)) {
        return component; // Return the string as-is
      }
      // Try to extract from parsed object
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        const extracted = extractComponentFromObject(parsed as Record<string, unknown>);
        if (extracted) {
          return typeof extracted === 'string' ? extracted : JSON.stringify(extracted);
        }
      }
    } catch {
      // Not valid JSON, but might still be a valid component string format
      // Let it pass through - C1Component can handle string formats
      return component;
    }
    return null;
  }
  
  // If it's already a valid C1ComponentObject
  if (isC1ComponentObject(component)) {
    return component;
  }
  
  // Try to extract from object structure (might be wrapped)
  // This handles the API format: { component: {...}, error: null }
  if (typeof component === 'object' && component !== null && !Array.isArray(component)) {
    const extracted = extractComponentFromObject(component as Record<string, unknown>);
    if (extracted) {
      return extracted;
    }
  }
  
  // If it's an array, try to extract the first valid component
  if (Array.isArray(component) && component.length > 0) {
    for (const item of component) {
      const extracted = extractComponent(item);
      if (extracted) {
        return extracted;
      }
    }
  }
  
  return null;
}

/**
 * Try to extract a C1Component from an object that might wrap it.
 * The API may return components wrapped as { component: {...}, error: null }
 * We extract the inner component since C1Component type expects C1ComponentObject | string
 */
function extractComponentFromObject(obj: Record<string, unknown>): C1Component | null {
  // Check if the object itself is a valid component
  if (isC1ComponentObject(obj)) {
    return obj;
  }
  
  // Check if this is a wrapper format: { component: {...}, error: ... }
  // Extract the inner component from the wrapper
  if ('component' in obj) {
    const innerComponent = obj.component;
    
    // If inner component is already a valid C1ComponentObject, return it
    if (isC1ComponentObject(innerComponent)) {
      return innerComponent;
    }
    
    // If inner component is a string, try to parse it
    if (typeof innerComponent === 'string') {
      try {
        const parsed = JSON.parse(innerComponent);
        if (isC1ComponentObject(parsed)) {
          return parsed;
        }
        // If parsed is still wrapped, recurse
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          const extracted = extractComponentFromObject(parsed as Record<string, unknown>);
          if (extracted) {
            return extracted;
          }
        }
      } catch {
        // Not JSON, but might be a valid component string format
        // Return as-is since C1Component can be a string
        return innerComponent;
      }
    }
    
    // If inner component is an object, recurse to extract
    if (typeof innerComponent === 'object' && innerComponent !== null && !Array.isArray(innerComponent)) {
      const extracted = extractComponentFromObject(innerComponent as Record<string, unknown>);
      if (extracted) {
        return extracted;
      }
    }
  }
  
  // Check other common wrapper properties
  const possibleKeys = ['c1_component', 'data', 'value', 'content'];
  for (const key of possibleKeys) {
    if (key in obj) {
      const value = obj[key];
      if (isC1ComponentObject(value)) {
        return value;
      }
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          if (isC1ComponentObject(parsed)) {
            return parsed;
          }
        } catch {
          // Not JSON, continue
        }
      }
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const extracted = extractComponentFromObject(value as Record<string, unknown>);
        if (extracted) {
          return extracted;
        }
      }
    }
  }
  
  return null;
}

/**
 * Filter out invalid components from an array.
 * 
 * @param components - Array of components to filter
 * @returns Array containing only valid C1Components
 */
function filterValidComponents(components: unknown[]): C1Component[] {
  if (!Array.isArray(components)) {
    console.warn('[Chat] filterValidComponents received non-array:', components);
    return [];
  }
  
  const valid: C1Component[] = [];
  const invalid: unknown[] = [];
  
  components.forEach((component, index) => {
    const extracted = extractComponent(component);
    if (extracted) {
      valid.push(extracted);
      // Log successful extraction for debugging
      if (typeof component === 'object' && component !== null && 'component' in component) {
        console.log(`[Chat] Successfully extracted component at index ${index} from wrapper format`);
      }
    } else {
      invalid.push({ 
        index, 
        component, 
        reason: 'could not extract valid C1Component',
        type: typeof component,
        isArray: Array.isArray(component),
        hasComponentKey: typeof component === 'object' && component !== null && 'component' in component,
      });
    }
  });
  
  if (invalid.length > 0) {
    console.warn(`[Chat] Filtered out ${invalid.length} invalid component(s):`, invalid);
    // Log the actual component structure for debugging
    invalid.forEach((item: any) => {
      console.warn(`[Chat] Invalid component at index ${item.index}:`, {
        component: item.component,
        reason: item.reason,
        type: item.type,
        isArray: item.isArray,
        componentString: typeof item.component === 'string' 
          ? item.component.substring(0, 200) 
          : item.component !== null && item.component !== undefined
            ? JSON.stringify(item.component, null, 2).substring(0, 500)
            : String(item.component),
      });
    });
  }
  
  return valid;
}

/**
 * Generate a unique ID for a component for deduplication purposes.
 * Uses a hash of the component's structure to identify duplicates.
 * 
 * @param component - C1 component to generate ID for
 * @returns Unique component ID string
 */
export function getComponentId(component: C1Component): string {
  try {
    // Create a stable representation of the component
    // Use a helper function to recursively sort object keys
    const sortKeys = (obj: unknown): unknown => {
      if (obj === null || typeof obj !== 'object') {
        return obj;
      }
      if (Array.isArray(obj)) {
        return obj.map(sortKeys);
      }
      const sorted: Record<string, unknown> = {};
      Object.keys(obj).sort().forEach((key: string) => {
        sorted[key] = sortKeys((obj as Record<string, unknown>)[key]);
      });
      return sorted;
    };
    
    const sortedComponent = sortKeys(component);
    const componentStr = JSON.stringify(sortedComponent);
    
    // Generate simple hash
    let hash = 0;
    for (let i = 0; i < componentStr.length; i++) {
      const char = componentStr.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(36);
  } catch (e) {
    console.warn("[Chat] Failed to generate component ID:", e);
    // Fallback: use string representation hash
    const str = JSON.stringify(component);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }
}

/**
 * Merge components, updating existing ones instead of filtering duplicates.
 * 
 * ⚠️ AI AGENT NOTE - CRITICAL:
 * This function UPDATES existing components, it does NOT deduplicate them.
 * If a component with the same structure exists, it REPLACES it with the newer version.
 * This allows updated components (e.g., with summaries, additional props) to replace older versions.
 * 
 * This is the CORRECT function to use when merging components from SSE streams or POST responses
 * with existing components in the UI. It ensures components are updated, not filtered out.
 * 
 * @param existingComponents - Components already in the message
 * @param newComponents - New components to merge in (will UPDATE matching existing ones)
 * @returns Merged array of components (existing ones updated, new ones added)
 */
export function mergeComponents(
  existingComponents: C1Component[],
  newComponents: C1Component[]
): C1Component[] {
  // First filter out invalid components
  const validExisting = filterValidComponents(existingComponents as unknown[]);
  const validNew = filterValidComponents(newComponents as unknown[]);
  
  const result: C1Component[] = [];
  const seenStructures = new Map<string, number>(); // Map normalized structure to index in result
  const seenSignatures = new Map<string, number>(); // Map component signature to index (for Card matching)
  
  // First, add all existing components to result
  validExisting.forEach((component) => {
    try {
      const normalized = normalizeComponentForComparison(component);
      const normalizedStr = JSON.stringify(normalized);
      const structureKey = normalizedStr;
      const signature = normalized._signature;
      
      // Track this structure
      seenStructures.set(structureKey, result.length);
      if (signature && typeof signature === 'string') {
        seenSignatures.set(signature, result.length);
      }
      result.push(component);
    } catch (error) {
      console.warn('[Chat] Skipping invalid component in merge:', error, component);
    }
  });
  
  // Then, process new components - update existing or add new
  validNew.forEach((component) => {
    try {
      const normalized = normalizeComponentForComparison(component);
      const normalizedStr = JSON.stringify(normalized);
      const structureKey = normalizedStr;
      const signature = normalized._signature;
      
      let existingIndex: number | undefined;
      
      // First try exact structure match
      if (seenStructures.has(structureKey)) {
        existingIndex = seenStructures.get(structureKey);
      } 
      // Then try signature match (for Card components with same title)
      else if (signature && typeof signature === 'string' && seenSignatures.has(signature)) {
        existingIndex = seenSignatures.get(signature);
      }
      
      if (existingIndex !== undefined) {
        // Update existing component with new version (in case it has more data)
        result[existingIndex] = component; // Replace with newer version
      } else {
        // New unique component
        seenStructures.set(structureKey, result.length);
        if (signature && typeof signature === 'string') {
          seenSignatures.set(signature, result.length);
        }
        result.push(component);
      }
    } catch (error) {
      console.warn('[Chat] Skipping invalid component in merge:', error, component);
    }
  });
  
  return result;
}

/**
 * Deduplicate components within a single array (removes exact duplicates).
 * 
 * ⚠️ AI AGENT NOTE:
 * This function REMOVES duplicates within a single array. It should NOT be used
 * when merging components from SSE streams or POST responses with existing UI components.
 * 
 * Use `mergeComponents()` instead when you need to UPDATE existing components in the UI.
 * This function is only for cleaning up exact duplicates within a single source array.
 * 
 * @param components - Components to deduplicate (removes exact duplicates only)
 * @returns Deduplicated array of components
 */
export function deduplicateComponents(components: C1Component[]): C1Component[] {
  // First filter out invalid components
  const validComponents = filterValidComponents(components as unknown[]);
  
  const seen = new Set<string>();
  const seenStructures = new Map<string, C1ComponentObject>(); // Track component structures for deep comparison
  
  return validComponents.filter((component) => {
    try {
      const componentId = getComponentId(component);
      
      // First check: same ID
      if (seen.has(componentId)) {
        return false;
      }
      
      // Second check: deep comparison of component structure
      // Normalize the component for comparison (remove any transient fields)
      const normalized = normalizeComponentForComparison(component);
      const normalizedStr = JSON.stringify(normalized);
      
      // Check if we've seen this exact structure before
      for (const [existingId, existingNormalized] of Array.from(seenStructures.entries())) {
        if (JSON.stringify(existingNormalized) === normalizedStr) {
          return false;
        }
      }
      
      // This is a new unique component
      seen.add(componentId);
      seenStructures.set(componentId, normalized);
      return true;
    } catch (error) {
      // Log and skip invalid components instead of crashing
      console.warn('[Chat] Skipping invalid component during deduplication:', error, component);
      return false;
    }
  });
}

/**
 * Normalize component for comparison (remove transient fields).
 * 
 * @param component - Component to normalize
 * @returns Normalized component object
 */
function normalizeComponentForComparison(component: C1Component): C1ComponentObject {
  if (typeof component === 'string') {
    try {
      const parsed = JSON.parse(component);
      return normalizeComponentForComparison(parsed);
    } catch {
      throw new Error('Invalid component string');
    }
  }
  
  if (!isC1ComponentObject(component)) {
    throw new Error('Invalid component structure');
  }
  
  // Extract just the component structure, ignoring wrapper fields like 'error'
  const comp = component;
  
  // Deep clone and normalize the component structure
  const normalize = (obj: unknown): unknown => {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    if (Array.isArray(obj)) {
      return obj.map(normalize);
    }
    const normalized: Record<string, unknown> = {};
    // Sort keys for consistent comparison
    const objRecord = obj as Record<string, unknown>;
    Object.keys(objRecord).sort().forEach(key => {
      // Skip error field and other transient fields
      if (key !== 'error' && key !== '_id' && key !== 'id') {
        normalized[key] = normalize(objRecord[key]);
      }
    });
    return normalized;
  };
  
  // Return the normalized component structure for comparison
  // Focus on the core component structure (component type and props)
  const normalized = normalize({
    component: comp.component,
    props: comp.props,
  }) as C1ComponentObject;
  
  // For Card components, also extract a "signature" based on title/header for better matching
  // This helps match cards that are the same even if there are minor prop differences
  if (normalized.component === 'Card' && normalized.props?.children) {
    const children = Array.isArray(normalized.props.children) ? normalized.props.children : [];
    const header = children.find((c) => 
      isC1ComponentObject(c) && c.component === 'Header'
    );
    
    if (header?.props?.title) {
      // Add a signature based on the card title for easier matching
      normalized._signature = `Card:${header.props.title}`;
    }
  }
  
  return normalized;
}

